/** Helpers for invited users creating an account (join existing org, not self-serve trial). */

export function parseInviteRegistrationContext(searchParams) {
  const redirect = searchParams.get('redirect') || '';
  const inviteToken =
    searchParams.get('invite_token') ||
    searchParams.get('token') ||
    (redirect.match(/[?&]token=([^&]+)/)?.[1] ?? '');
  const emailParam = searchParams.get('email') || '';

  return {
    isInviteRegistration: Boolean(inviteToken),
    inviteToken,
    emailParam: emailParam ? decodeURIComponent(emailParam) : '',
    redirectPath: redirect.startsWith('/') ? redirect : '',
  };
}

export async function fetchInviteRegistrationDetails(apiBaseUrl, inviteToken) {
  if (!inviteToken) {
    return null;
  }
  const response = await fetch(
    `${apiBaseUrl}/organizations/invitations/${encodeURIComponent(inviteToken)}`,
  );
  if (!response.ok) {
    return null;
  }
  return response.json();
}

export function parseInviteLoginContext(searchParams) {
  const redirect = searchParams.get('redirect') || '';
  const inviteToken =
    searchParams.get('invite_token') ||
    (redirect.match(/[?&]token=([^&]+)/)?.[1] ?? '');
  return {
    isInviteLogin: Boolean(inviteToken),
    inviteToken,
    redirectPath: redirect.startsWith('/') ? redirect : '',
  };
}

export async function fetchInviteOrganizationHint(apiBaseUrl, inviteToken) {
  const details = await fetchInviteRegistrationDetails(apiBaseUrl, inviteToken);
  if (!details?.organization) {
    return null;
  }
  return {
    id: details.organization.id ? String(details.organization.id) : null,
    name: details.organization.name || null,
    role: details.role || null,
  };
}

export function pickPreferredOrganizationId(organizations, preferredOrgId) {
  if (!organizations?.length) {
    return '';
  }
  if (preferredOrgId) {
    const match = organizations.find((org) => String(org.id) === String(preferredOrgId));
    if (match) {
      return String(match.id);
    }
  }
  return String(organizations[0].id);
}

export function organizationOptionLabel(org) {
  const name = org.name || org.slug || `Organization ${org.id}`;
  return org.role ? `${name} (${org.role})` : name;
}

export function buildInviteLoginUrl(token) {
  const inviteRedirect = token ? `/invite?token=${encodeURIComponent(token)}` : '/invite';
  return `/login?redirect=${encodeURIComponent(inviteRedirect)}`;
}

/** After invite signup the invite is already accepted — send users to the app, not back to /invite. */
export function buildInviteRegisterUrl({ token, email, postRegisterPath = '/' }) {
  const params = new URLSearchParams();
  if (token) {
    params.set('invite_token', token);
    params.set('redirect', postRegisterPath);
  }
  if (email) {
    params.set('email', email);
  }
  return `/register?${params.toString()}`;
}
